import React, { useEffect, useState, useCallback } from "react";
import { API } from "../../config/api";
import "./../Styles/UserTransaction.css";

export default function UserTransaction() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  // Fallback to user.id if sessionStorage userId key is missing/empty
  const userId = sessionStorage.getItem("userId") || user?.id || "";

  const [machineTransactions, setMachineTransactions] = useState([]);
  const [workspaceTransactions, setWorkspaceTransactions] = useState([]);
  const [serverTransactions, setServerTransactions] = useState([]);
  const [workTransactions, setWorkTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");

  const getRoles = () => {
    let options = user.provider_options || [];

    if (typeof options === "string") {
      try {
        options = JSON.parse(options);
      } catch {
        options = options.split(",");
      }
    }

    return Array.isArray(options)
      ? options.map((r) => String(r).trim().toLowerCase())
      : [];
  };

  const roles = getRoles();
  const canMachine = roles.includes("machines");
  const canWorkspace = roles.includes("workspaces");
  const canDesigns = roles.includes("designs");
  const canWork = roles.includes("job_worker");
  const fetchTransactions = useCallback(async () => {
    setLoading(true);

    try {
      if (canMachine) {
        const res = await fetch(
          `${API.GET_MACHINE_TRANSACTIONS}&user_id=${userId}`,
        );
        const data = await res.json();
        setMachineTransactions(data.status ? data.data : []);
      }

      if (canWorkspace) {
        const res = await fetch(
          `${API.GET_WORKSPACE_TRANSACTIONS}&user_id=${userId}`,
        );
        const data = await res.json();
        setWorkspaceTransactions(data.status ? data.data : []);
      }

      if (canDesigns) {
        const serverRes = await fetch(
          `${API.BASE_URL}/payment.php?action=server_charge_history&user_id=${userId}`,
        );

        const serverData = await serverRes.json();

        setServerTransactions(serverData.success ? serverData.payments : []);
      }
      if (canWork) {
        const workRes = await fetch(
          `${API.GET_WORK_TRANSACTIONS}&user_id=${userId}`,
        );

        const workData = await workRes.json();

        setWorkTransactions(workData.status ? workData.data : []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId, canMachine, canWorkspace, canDesigns, canWork]);
  useEffect(() => {
    if (userId) {
      fetchTransactions();
    } else {
      setLoading(false);
    }
  }, [userId, fetchTransactions]);
  // Safe mapping helper to get asset/item name regardless of API key variations
  const getItemName = (item, type) => {
    if (type === "Machine") {
      return item.machine_name || item.title || item.name || "-";
    }
    return item.workspace_name || item.title || item.name || "-";
  };

  // Safe mapping for transaction date
  const getItemDate = (item) => {
    const rawDate = item.created_at || item.date || item.created_date;
    if (!rawDate) return "-";
    const parsed = new Date(rawDate);
    return isNaN(parsed.getTime())
      ? rawDate
      : parsed.toLocaleDateString("en-GB");
  };

  // Safely combine both lists
  const combinedTransactions = [
    ...(canMachine
      ? machineTransactions.map((item) => ({
          ...item,
          txn_category: "Machine",
          asset_name: getItemName(item, "Machine"),
        }))
      : []),

    ...(canWorkspace
      ? workspaceTransactions.map((item) => ({
          ...item,
          txn_category: "Workspace",
          asset_name: getItemName(item, "Workspace"),
        }))
      : []),

    ...(canDesigns
      ? serverTransactions.map((item) => ({
          ...item,
          txn_category: "Server Charge",
          razorpay_payment_id: item.payment_id,
        }))
      : []),

    ...workTransactions.map((item) => ({
      ...item,
      txn_category: "Work Payment",
      txnid: item.txnid,
      amount: item.amount,
      status: item.payment_status,
    })),
  ].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Filter list by selected dropdown value
  const filteredTransactions = combinedTransactions.filter((item) => {
    if (filterType === "machine") return item.txn_category === "Machine";

    if (filterType === "workspace") return item.txn_category === "Workspace";

    if (filterType === "server") return item.txn_category === "Server Charge";
    if (filterType === "work") return item.txn_category === "Work Payment";
    return true;
  });

  const getStatusClass = (status) => {
    const strStatus = String(status).toLowerCase();
    if (strStatus === "paid") return "utx-status-success";
    if (strStatus === "success") return "utx-status-success";
    if (strStatus === "pending") return "utx-status-pending";
    return "utx-status-danger";
  };
  const typeStyle = (type) => {
    switch (type) {
      case "Machine":
        return { background: "#dcfce7", color: "#15803d" };

      case "Workspace":
        return { background: "#ede9fe", color: "#7c3aed" };

      case "Server Charge":
        return { background: "#fff7ed", color: "#ea580c" };

      case "Work Payment":
        return { background: "#fef3c7", color: "#b45309" };

      default:
        return { background: "#f3f4f6", color: "#374151" };
    }
  };
  if (loading) {
    return (
      <div className="container-fluid p-4 utx-page-container">
        <div className="utx-loading-box">
          <h4>Loading transaction details...</h4>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 utx-page-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h2 className="utx-page-title fw-bold mb-0">Transaction Details</h2>

        {canMachine || canWorkspace || canDesigns ? (
          <select
            className="form-select utx-filter-select"
            style={{ width: "230px" }}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Transactions</option>

            {canMachine && <option value="machine">Machine</option>}

            {canWorkspace && <option value="workspace">Workspace</option>}

            {canDesigns && <option value="server">Server Charge</option>}
            {canWork && <option value="work">Work Payment</option>}
          </select>
        ) : (
          <select
            className="form-select utx-filter-select"
            style={{ width: "230px" }}
            disabled
          >
            <option>
              {canMachine ? "Machine Transactions" : "Workspace Transactions"}
            </option>
          </select>
        )}
      </div>

      {/* Unified Table Card */}
      <div className="card utx-card p-0">
        <div className="card-body p-0 w-100">
          <div className="table-responsive mb-0">
            <table className="table align-middle utx-table">
              <thead className="utx-table-head">
                <tr className="text-center">
                  <th>#</th>
                  <th>Date</th>
                  <th>Type</th>

                  <th>Amount</th>
                  <th>Payment ID</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((item, index) => (
                    <tr
                      key={`${item.txn_category}-${item.id || index}`}
                      className="utx-table-row"
                    >
                      <td className="utx-cell-muted">{index + 1}</td>
                      <td>
                        <span className="utx-cell-bold">
                          {getItemDate(item)}
                        </span>
                      </td>
                      <td>
                        <span
                          className="txnp-type-pill"
                          style={typeStyle(item.txn_category)}
                        >
                          {item.txn_category}
                        </span>
                      </td>
                      <td className="utx-amount-val">
                        ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                      </td>
                      {/* <td className="utx-payment-code">
                        {item.razorpay_payment_id || item.txnid || "-"}
                      </td> */}
                      <td className="utx-payment-code">{item.txnid || "-"}</td>
                      <td>
                        <span
                          className={`utx-status-pill ${getStatusClass(
                            item.status,
                          )}`}
                        >
                          {item.status || ""}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-5">
                      No Transactions Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
